import { Box, Grid, Skeleton, Typography } from '@mui/material';
import { PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { useDashboard } from '../DashboardContext';
import { useTranslation } from 'react-i18next';
import DashboardWidget, { DashboardSizedChart } from './DashboardWidget';

const COLORS = ['#2e7d32', '#0288d1', '#ed6c02', '#d32f2f', '#7b1fa2', '#00695c', '#c2185b', '#f57c00', '#455a64', '#6d4c41'];

const formatShort = (value: number) => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return value.toFixed(0);
};

const formatTooltipValue = (value: number, currency: string) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value) + ` ${currency}`;

const SalesBreakdownBarPanel = ({
  title,
  helpKey,
  data,
  dataKey,
  color,
  currency,
}: {
  title: string;
  helpKey: string;
  data: { amount: number }[];
  dataKey: string;
  color: string;
  currency: string;
}) => {
  const { t } = useTranslation();

  return (
    <DashboardWidget title={title} helpKey={helpKey} chartHeight={300}>
      {data.length > 0 ? (
        <DashboardSizedChart height={300}>
          <BarChart data={data.slice(0, 10)} layout="vertical" margin={{ left: 20, right: 20 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis type="number" tickFormatter={formatShort} />
            <YAxis type="category" dataKey={dataKey} width={100} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(value: number) => formatTooltipValue(value, currency)} />
            <Bar dataKey="amount" fill={color} radius={[0, 4, 4, 0]} />
          </BarChart>
        </DashboardSizedChart>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 200, color: 'text.secondary' }}>
          <Typography variant="body2">{t('common:dashboard.noData')}</Typography>
        </Box>
      )}
    </DashboardWidget>
  );
};

const SalesBySectorPanel = ({ data, currency }: { data: { sector: string; amount: number }[]; currency: string }) => {
  const { t } = useTranslation();

  return (
    <DashboardWidget
      title={t('common:dashboard.salesBreakdown.bySector')}
      helpKey="salesBreakdown.bySector"
      chartHeight={300}
    >
      {data.length > 0 ? (
        <DashboardSizedChart height={300}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              outerRadius={100}
              paddingAngle={2}
              dataKey="amount"
              nameKey="sector"
              label={({ sector, percent }) => `${sector} ${(percent * 100).toFixed(0)}%`}
            >
              {data.map((_, idx) => (
                <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value: number) => formatTooltipValue(value, currency)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
          </PieChart>
        </DashboardSizedChart>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 200, color: 'text.secondary' }}>
          <Typography variant="body2">{t('common:dashboard.noData')}</Typography>
        </Box>
      )}
    </DashboardWidget>
  );
};

const SalesByAgentPanel = ({ data, currency }: { data: { agent: string; amount: number }[]; currency: string }) => {
  const { t } = useTranslation();

  return (
    <DashboardWidget
      title={t('common:dashboard.salesBreakdown.byAgent')}
      helpKey="salesBreakdown.byAgent"
      chartHeight={300}
    >
      {data.length > 0 ? (
        <DashboardSizedChart height={300}>
          <BarChart data={data} layout="vertical" margin={{ left: 20, right: 20 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis type="number" tickFormatter={formatShort} />
            <YAxis type="category" dataKey="agent" width={100} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(value: number) => formatTooltipValue(value, currency)} />
            <Bar dataKey="amount" fill="#0288d1" radius={[0, 4, 4, 0]} />
          </BarChart>
        </DashboardSizedChart>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 200, color: 'text.secondary' }}>
          <Typography variant="body2">{t('common:dashboard.noData')}</Typography>
        </Box>
      )}
    </DashboardWidget>
  );
};

const SalesBreakdown = () => {
  const {
    salesByFamily,
    salesByCategory,
    salesByDepot,
    salesBySector,
    salesByAgent,
    sectionLoading,
    currency,
  } = useDashboard();
  const { t } = useTranslation();

  if (sectionLoading.sales) {
    return (
      <Grid container spacing={2}>
        {Array.from({ length: 5 }).map((_, idx) => (
          <Grid key={idx} size={{ xs: 12, md: idx < 3 ? 4 : 6 }}>
            <DashboardWidget chartHeight={300}>
              <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />
            </DashboardWidget>
          </Grid>
        ))}
      </Grid>
    );
  }

  const barPanels = [
    {
      title: t('common:dashboard.salesBreakdown.byFamily'),
      helpKey: 'salesBreakdown.byFamily',
      data: salesByFamily,
      dataKey: 'family',
      color: '#7b1fa2',
    },
    {
      title: t('common:dashboard.salesBreakdown.byCategory'),
      helpKey: 'salesBreakdown.byCategory',
      data: salesByCategory,
      dataKey: 'category',
      color: '#0288d1',
    },
    {
      title: t('common:dashboard.salesBreakdown.byDepot'),
      helpKey: 'salesBreakdown.byDepot',
      data: salesByDepot,
      dataKey: 'depot',
      color: '#2e7d32',
    },
  ] as const;

  return (
    <Grid container spacing={2}>
      {barPanels.map((panel) => (
        <Grid key={panel.dataKey} size={{ xs: 12, md: 4 }}>
          <SalesBreakdownBarPanel {...panel} currency={currency} />
        </Grid>
      ))}

      <Grid size={{ xs: 12, md: 6 }}>
        <SalesBySectorPanel data={salesBySector} currency={currency} />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <SalesByAgentPanel data={salesByAgent} currency={currency} />
      </Grid>
    </Grid>
  );
};

export default SalesBreakdown;